import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import type { EgressKindDto, StaticIpDto } from '@apna/shared'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

const field =
  'h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 disabled:opacity-50'
const lbl = 'grid gap-1.5 text-[11px] font-medium text-muted-foreground'

const KINDS: { value: EgressKindDto; label: string; hint: string }[] = [
  { value: 'HTTP_PROXY', label: 'HTTP proxy', hint: 'A CONNECT proxy. The usual shape a static-IP provider sells.' },
  { value: 'SOCKS5', label: 'SOCKS5', hint: 'A SOCKS5 proxy, authenticated or not.' },
  { value: 'DIRECT', label: 'Direct', hint: 'No proxy — traffic leaves from this host. Takes no host or port.' },
]

export interface StaticIpForm {
  label: string
  kind: EgressKindDto
  host: string
  port: string
  expectedExitIp: string
  validityEndsAt: string
  source: 'PLATFORM_MANAGED' | 'USER_SUPPLIED'
  isActive: boolean
  username: string
  password: string
}

const blank: StaticIpForm = {
  label: '', kind: 'HTTP_PROXY', host: '', port: '', expectedExitIp: '',
  validityEndsAt: '', source: 'USER_SUPPLIED', isActive: true, username: '', password: '',
}

/**
 * Add or edit one static IP.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A BLANK SECRET MEANS KEEP, AND THE FORM HAS TO SAY SO
 *
 * An existing username or password cannot be shown — it is encrypted and
 * never leaves the server — so the box renders empty on an edit. If empty
 * meant "clear", opening a proxy and saving it without retyping would
 * silently strip its credentials, and the symptom would arrive later as a
 * refused order from a proxy that stopped authenticating.
 *
 * So on an edit these fields are sent ONLY when typed into, and the
 * placeholder says what blank does. Clearing is deliberate: type a space.
 *
 * EXPECTED EXIT IP IS THE POINT OF THE WHOLE RECORD. It is what the broker
 * whitelists and what a verification compares against — an endpoint without
 * one can never be verified, because there is nothing to check.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function StaticIpDialog({
  open,
  editing,
  onClose,
  onSave,
}: {
  open: boolean
  /** Null means "add". */
  editing: StaticIpDto | null
  onClose: () => void
  onSave: (form: StaticIpForm, editingId: string | null) => Promise<void>
}) {
  const [form, setForm] = useState<StaticIpForm>(blank)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setError(null)
    setForm(
      editing
        ? {
            label: editing.label,
            kind: editing.kind,
            host: editing.host ?? '',
            port: editing.port ? String(editing.port) : '',
            expectedExitIp: editing.expectedExitIp ?? '',
            // `date` wants YYYY-MM-DD and the wire carries an ISO instant.
            validityEndsAt: editing.validityEndsAt ? editing.validityEndsAt.slice(0, 10) : '',
            source: editing.source,
            isActive: editing.isActive,
            username: '',
            password: '',
          }
        : blank,
    )
  }, [editing, open])

  if (!open) return null

  const set = <K extends keyof StaticIpForm>(k: K, v: StaticIpForm[K]) => setForm((f) => ({ ...f, [k]: v }))
  const isDirect = form.kind === 'DIRECT'

  const submit = async () => {
    if (saving) return
    setSaving(true)
    setError(null)
    try {
      await onSave(form, editing?.id ?? null)
      onClose()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not save this static IP.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving) onClose() }}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-[560px]" showCloseButton={!saving}>
        <header className="border-b border-border px-5 py-3.5">
          <DialogTitle className="text-[15px] font-semibold">{editing ? 'Edit static IP' : 'Add static IP'}</DialogTitle>
          <DialogDescription className="mt-1 text-[12px] text-muted-foreground">
            The address your broker whitelists. Orders for the accounts assigned to it leave from here.
          </DialogDescription>
        </header>

        <form onSubmit={(e) => { e.preventDefault(); void submit() }} className="max-h-[62vh] overflow-y-auto p-5">
          <fieldset disabled={saving} className="grid gap-4">
            <div className="grid grid-cols-2 gap-3">
              <label className={lbl}>Label
                <input className={field} value={form.label} data-testid="sip-label" required maxLength={60}
                  placeholder="Mumbai A" onChange={(e) => set('label', e.target.value)} />
              </label>
              <label className={lbl}>Type
                <select className={field} value={form.kind} data-testid="sip-kind"
                  onChange={(e) => set('kind', e.target.value as EgressKindDto)}>
                  {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                </select>
              </label>
            </div>
            <p className="-mt-2 text-[10px] text-muted-foreground">{KINDS.find((k) => k.value === form.kind)?.hint}</p>

            {!isDirect && (
              <div className="grid grid-cols-[1.6fr_1fr] gap-3">
                <label className={lbl}>Host <span className="text-destructive">*</span>
                  <input className={field} value={form.host} data-testid="sip-host" required
                    placeholder="dc-mum-002.staticip.in" onChange={(e) => set('host', e.target.value)} />
                </label>
                <label className={lbl}>Port <span className="text-destructive">*</span>
                  <input className={cn(field, 'num')} value={form.port} data-testid="sip-port" required
                    type="number" min={1} max={65535} placeholder="443" onChange={(e) => set('port', e.target.value)} />
                </label>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <label className={lbl}>Expected exit IP
                <input className={cn(field, 'num')} value={form.expectedExitIp} data-testid="sip-exit-ip"
                  placeholder="157.20.241.146" onChange={(e) => set('expectedExitIp', e.target.value)} />
              </label>
              <label className={lbl}>Validity ends
                <input className={field} type="date" value={form.validityEndsAt} data-testid="sip-validity"
                  onChange={(e) => set('validityEndsAt', e.target.value)} />
              </label>
            </div>
            <p className="-mt-2 text-[10px] text-muted-foreground">
              The exit IP is what the broker whitelists and what a verification checks against. Leave the date blank
              for an address with no rental end.
            </p>

            {!isDirect && (
              <div className="grid grid-cols-2 gap-3">
                <label className={lbl}>Username
                  <input className={field} value={form.username} data-testid="sip-username" autoComplete="off"
                    placeholder={editing ? 'Leave blank to keep' : 'Optional'}
                    onChange={(e) => set('username', e.target.value)} />
                </label>
                <label className={lbl}>Password
                  <input className={field} value={form.password} data-testid="sip-password" type="password" autoComplete="new-password"
                    placeholder={editing ? 'Leave blank to keep' : 'Optional'}
                    onChange={(e) => set('password', e.target.value)} />
                </label>
              </div>
            )}

            <label className="flex items-center gap-2 text-[12px]">
              <input type="checkbox" checked={form.isActive} data-testid="sip-active"
                onChange={(e) => set('isActive', e.target.checked)} />
              Active — an inactive endpoint is ignored and its accounts fall back to the platform address
            </label>
          </fieldset>

          {error && (
            <p role="alert" className="mt-3 rounded border border-destructive/20 bg-destructive/5 p-2 text-[11px] text-destructive">
              {error}
            </p>
          )}

          <footer className="mt-4 flex justify-end gap-2">
            <button type="button" disabled={saving} onClick={onClose}
              className="rounded-md border border-border px-3 py-2 text-[12px] font-medium disabled:opacity-40">Cancel</button>
            <button type="submit" disabled={saving} data-testid="sip-save"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-[12px] font-semibold text-primary-foreground disabled:opacity-50">
              {saving && <Loader2 size={13} className="animate-spin" />}
              {saving ? 'Saving…' : editing ? 'Update static IP' : 'Add static IP'}
            </button>
          </footer>
        </form>
      </DialogContent>
    </Dialog>
  )
}
